import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Attachment } from '~/containers/NewsEntryForm/Attachments';
import { NewsEntriesGetData } from '~/generated';
import { client } from '~/generated/client.gen';

export type NewsEntry = {
  id: number;
  title: string;
  date: string;
  content: string;
  revision: number;
  isRead: 'Unread' | 'ReadButChanged' | 'Read';
  attachments: Attachment[];
  visibility: string[];
  validFrom: string;
  validUntil: string | null;
};

export const newsEntriesGet = async (
  data: Omit<NewsEntriesGetData, 'url'> = {}
): Promise<NewsEntry[]> => {
  // Use the client directly to get access to response headers
  const response = await client.get({
    url: '/NewsEntries',
    query: data.query,
    responseType: 'json',
  });
  if (isAxiosError(response)) {
    throw response;
  }
  // Type guard to ensure we have a successful response
  if (!('data' in response)) {
    throw new Error('Unexpected response type');
  }

  // Return the data from the response
  return response.data as unknown as NewsEntry[];
};

const getTotalCount = async (
  options: Omit<NewsEntriesGetData, 'url'>
): Promise<string> => {
  const response = await client.get({
    url: '/NewsEntries',
    query: options.query,
    responseType: 'json',
  });
  if (isAxiosError(response)) {
    throw response;
  }
  if (!('headers' in response) || !('data' in response)) {
    throw new Error('Unexpected response type');
  }
  return response.headers['total-count'] as unknown as string;
};

type NewsEntriesResult = {
  newsEntries: NewsEntry[];
  totalCount: number;
};

export const useNewsEntries = (
  options: Omit<NewsEntriesGetData, 'url'> = {},
  queryOptions?: Omit<
    UseQueryOptions<NewsEntriesResult, unknown, NewsEntriesResult, unknown[]>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery({
    queryFn: async () => {
      const totalCount = await getTotalCount(options);
      const response = await newsEntriesGet(options);
      return {
        newsEntries: response,
        totalCount: parseInt(totalCount, 10),
      };
    },
    queryKey: ['NewsEntries', options],
    ...queryOptions,
  });
